import platformApi, {
  clearAuthStorage,
  getRefreshToken,
  setAuthTokens,
} from './api';
import {
  getPayloadData,
  getPayloadItems,
  isOrderHistoryDeal,
  isSuccessResponse,
  normalizeAccount,
  normalizeDeal,
  normalizeEnvelope,
  normalizeItemsEnvelope,
  normalizeOrder,
  normalizePosition,
  normalizePrice,
  normalizeSymbol,
  normalizeUser,
} from './normalizers';

const SUPPORTED_TIMEFRAMES = new Set([
  'M1',
  'M5',
  'M15',
  'M30',
  'H1',
  'H4',
  'D1',
  'W1',
  'MN1',
]);

const LEGACY_INTERVAL_TO_TIMEFRAME = {
  '1m': 'M1',
  '5m': 'M5',
  '15m': 'M15',
  '30m': 'M30',
  '1h': 'H1',
  '4h': 'H4',
  '1D': 'D1',
  '1W': 'W1',
  '1M': 'MN1',
};

const ORDER_TYPE_NAMES = [
  'BUY',
  'SELL',
  'BUY_LIMIT',
  'SELL_LIMIT',
  'BUY_STOP',
  'SELL_STOP',
  'BUY_STOP_LIMIT',
  'SELL_STOP_LIMIT',
  'CLOSE_BY',
];

const PENDING_ORDER_TYPES = new Set([
  'BUY_LIMIT',
  'SELL_LIMIT',
  'BUY_STOP',
  'SELL_STOP',
  'BUY_STOP_LIMIT',
  'SELL_STOP_LIMIT',
]);

const PRICE_SNAPSHOT_BATCH_SIZE = 40;

const toPositiveInteger = (value, fallback, maximum = 5000) => {
  const number = Math.floor(Number(value));
  if (!Number.isFinite(number) || number <= 0) return fallback;
  return Math.min(number, maximum);
};

const normalizeDealQuery = (limit = 100, days = 30, filters = {}) => {
  const query = (
    limit && typeof limit === 'object' && !Array.isArray(limit)
      ? limit
      : { ...filters, limit, days }
  );

  const params = {
    limit: toPositiveInteger(query.limit, 100),
    days: toPositiveInteger(query.days, 30, 3650),
  };

  ['symbol', 'order', 'type'].forEach(key => {
    const value = String(query[key] ?? '').trim();
    if (value && value.toLowerCase() !== 'all') params[key] = value;
  });

  return params;
};

const toTimeframe = (interval) => {
  const value = String(interval || 'M1');
  const normalized = LEGACY_INTERVAL_TO_TIMEFRAME[value] || value.toUpperCase();
  return SUPPORTED_TIMEFRAMES.has(normalized) ? normalized : 'M1';
};

const toOrderType = (value) => {
  if (typeof value === 'string' && Number.isNaN(Number(value))) {
    return value.toUpperCase();
  }

  const number = Number(value);
  return Number.isInteger(number) && ORDER_TYPE_NAMES[number]
    ? ORDER_TYPE_NAMES[number]
    : 'BUY';
};

const isPendingOrderPayload = (payload = {}) => {
  const orderType = toOrderType(
    payload.order_type ?? payload.orderType ?? payload.type ?? payload.action,
  );
  return PENDING_ORDER_TYPES.has(orderType);
};

const normalizeOrderRequest = (payload = {}) => {
  const body = { ...payload };
  body.order_type = toOrderType(
    body.order_type ?? body.orderType ?? body.type ?? body.action,
  );

  delete body.orderType;

  if (!isPendingOrderPayload(body) && !(Number(body.price) > 0)) {
    // The API resolves a missing market price from the current tick, while an
    // explicit zero is rejected as invalid.
    delete body.price;
  }

  return body;
};

const normalizeCandle = (value = {}) => {
  let timestamp = Number(value.timestamp ?? value.time ?? value.at ?? 0);
  if (timestamp > 0 && timestamp < 100000000000) timestamp *= 1000;

  return {
    ...value,
    timestamp,
    time: timestamp,
    open: Number(value.open) || 0,
    high: Number(value.high) || 0,
    low: Number(value.low) || 0,
    close: Number(value.close) || 0,
    tick_volume: Number(value.tick_volume ?? value.tickVolume) || 0,
    tickVolume: Number(value.tick_volume ?? value.tickVolume) || 0,
    volume: Number(value.volume) || 0,
    spread: Number(value.spread) || 0,
  };
};

const normalizeSingleResponse = (payload, normalizer) => {
  const data = getPayloadData(payload);
  return normalizeEnvelope(payload, normalizer(data || {}));
};

const normalizeArrayResponse = (payload, normalizer = value => value) => (
  normalizeEnvelope(payload, getPayloadItems(payload).map(normalizer))
);

const normalizeTradingListResponse = (payload, normalizer) => (
  normalizeItemsEnvelope(payload, normalizer)
);

const toClosePayload = (payload) => {
  if (typeof payload === 'number' || typeof payload === 'string') {
    return { volume: Number(payload) };
  }
  return payload && typeof payload === 'object' ? payload : {};
};

export const login = async (credentials) => {
  const response = await platformApi.post('/public/auth/login', credentials);
  const result = normalizeEnvelope(response.data);
  const tokens = getPayloadData(result);

  if (result.success && tokens?.access_token) {
    setAuthTokens(tokens, {
      clearMissingRefresh: true,
      reason: 'login',
    });
  }

  return result;
};

export const logout = async () => {
  clearAuthStorage('logout');
  return {
    status: true,
    success: true,
    message: 'Logged out',
    data: null,
  };
};

export const refreshToken = async (payload = {}) => {
  const token = payload.refresh_token || payload.refreshToken || getRefreshToken();
  if (!token) throw new Error('Missing refresh token');

  const response = await platformApi.post('/public/auth/refresh', {
    refresh_token: token,
  });
  const result = normalizeEnvelope(response.data);
  const tokens = getPayloadData(result);

  if (result.success && tokens?.access_token) {
    setAuthTokens(tokens, {
      clearMissingRefresh: true,
      reason: 'refresh',
    });
  }

  return result;
};

export const getHealth = async () => {
  const response = await platformApi.get('/system/health');
  return normalizeEnvelope(response.data);
};

export const health = getHealth;

export const getPublicSymbols = async () => {
  const response = await platformApi.get('/public/symbols');
  return normalizeArrayResponse(response.data, normalizeSymbol);
};

export const getSymbol = async (symbol) => {
  const response = await platformApi.get(
    `/public/symbols/${encodeURIComponent(symbol)}`,
  );
  return normalizeSingleResponse(response.data, normalizeSymbol);
};

export const getPublicLatestPrice = async (symbol) => {
  const response = await platformApi.get(
    `/public/prices/${encodeURIComponent(symbol)}/latest`,
  );
  return normalizeSingleResponse(response.data, normalizePrice);
};

export const getLastPrice = async (symbols = []) => {
  const values = Array.isArray(symbols)
    ? symbols
    : String(symbols || '').split(',');
  const symbolList = Array.from(new Set(
    values
      .map(symbol => String(symbol?.symbol || symbol || '').trim())
      .filter(Boolean),
  ));

  if (!symbolList.length) {
    return {
      status: true,
      success: true,
      message: 'No symbols available for price snapshot',
      data: [],
    };
  }

  const chunks = [];
  for (let index = 0; index < symbolList.length; index += PRICE_SNAPSHOT_BATCH_SIZE) {
    chunks.push(symbolList.slice(index, index + PRICE_SNAPSHOT_BATCH_SIZE));
  }

  const responses = await Promise.all(chunks.map(chunk => (
    platformApi.get('/public/prices/snapshot', {
      params: { symbols: chunk.join(',') },
    })
  )));
  const payloads = responses.map(response => response.data);
  const failedPayload = payloads.find(payload => !isSuccessResponse(payload));
  const prices = payloads.flatMap(payload => (
    getPayloadItems(payload).map(normalizePrice)
  ));
  const firstPayload = payloads[0] || {};
  const success = !failedPayload;

  return {
    ...normalizeEnvelope(firstPayload, prices),
    status: success,
    success,
    message: failedPayload?.message || firstPayload.message || 'Prices fetched successfully',
  };
};

export const getPriceSnapshot = getLastPrice;

export const getTimeframes = async () => {
  const response = await platformApi.get('/public/chart/timeframes');
  return normalizeArrayResponse(
    response.data,
    timeframe => String(timeframe || '').toUpperCase(),
  );
};

export const getChartTimeframes = getTimeframes;

export const getCandles = async (symbol, timeframe = 'M5', limit = 100) => {
  const response = await platformApi.get(
    `/public/chart/${encodeURIComponent(symbol)}/candles`,
    {
      params: {
        timeframe: toTimeframe(timeframe),
        limit: toPositiveInteger(limit, 100),
      },
    },
  );
  return normalizeArrayResponse(response.data, normalizeCandle);
};

export const getChartHistory = async (
  symbol,
  interval,
  _from,
  _to,
  _group,
) => {
  const response = await platformApi.get(
    `/public/chart/${encodeURIComponent(symbol)}/candles`,
    {
      params: {
        timeframe: toTimeframe(interval),
        limit: 500,
      },
    },
  );
  const normalized = normalizeArrayResponse(response.data, normalizeCandle);

  // This legacy method historically returned an Axios-like response object.
  return {
    ...response,
    data: normalized,
  };
};

export const getTicks = async (symbol, limit = 100) => {
  const response = await platformApi.get(
    `/public/chart/${encodeURIComponent(symbol)}/ticks`,
    { params: { limit: toPositiveInteger(limit, 100) } },
  );
  return normalizeArrayResponse(response.data, normalizePrice);
};

export const getChartTicks = getTicks;

export const getProfile = async () => {
  const response = await platformApi.get('/user/profile');
  return normalizeSingleResponse(response.data, normalizeUser);
};

export const getAccountMetrics = async () => {
  const response = await platformApi.get('/user/account/metrics');
  return normalizeSingleResponse(response.data, normalizeAccount);
};

export const getDetails = async () => {
  const [profileResponse, accountResponse] = await Promise.all([
    platformApi.get('/user/profile'),
    platformApi.get('/user/account/metrics'),
  ]);
  const profilePayload = profileResponse.data;
  const accountPayload = accountResponse.data;
  const rawUser = getPayloadData(profilePayload) || {};
  const rawAccount = getPayloadData(accountPayload) || {};
  let user = normalizeUser(rawUser, rawAccount);
  const account = normalizeAccount(rawAccount, user);
  user = normalizeUser(user, account);
  const success = isSuccessResponse(profilePayload) && isSuccessResponse(accountPayload);

  return {
    status: success,
    success,
    message: success
      ? 'Success'
      : (profilePayload?.message || accountPayload?.message || 'Failed to load account details'),
    data: {
      login: user.login || account.login,
      user,
      account,
    },
    time: Math.max(
      Number(profilePayload?.time) || 0,
      Number(accountPayload?.time) || 0,
    ),
  };
};

// Kept for existing callers; the registered equivalent is account/metrics.
export const getBalanceEquity = getAccountMetrics;

export const getSymbolList = async (_group) => {
  const response = await platformApi.get('/user/groups/symbols');
  return normalizeArrayResponse(response.data, normalizeSymbol);
};

export const getGroupSymbols = getSymbolList;

export const getDeals = async (limit = 100, days = 30, filters = {}) => {
  const response = await platformApi.get('/user/deals', {
    params: normalizeDealQuery(limit, days, filters),
  });
  return normalizeTradingListResponse(response.data, normalizeDeal);
};

export const getDealsHistory = async (limit = 500, days = 30, filters = {}) => (
  getDeals(limit, days, filters)
);

export const getUserLatestPrice = async (symbol) => {
  const response = await platformApi.get(
    `/user/prices/${encodeURIComponent(symbol)}/latest`,
  );
  return normalizeSingleResponse(response.data, normalizePrice);
};

export const getCloseOrders = async (limit = 100) => {
  const response = await platformApi.get('/user/close_orders', {
    params: { limit: toPositiveInteger(limit, 100) },
  });
  return normalizeTradingListResponse(response.data, normalizeOrder);
};

export const getClosedOrders = getCloseOrders;

export const getOrderHistory = async (limit = 500, days = 3650, filters = {}) => {
  const result = await getDeals(limit, days, filters);
  const items = getPayloadItems(result).filter(isOrderHistoryDeal);

  return {
    ...result,
    data: {
      ...(result.data && typeof result.data === 'object' && !Array.isArray(result.data)
        ? result.data
        : {}),
      items,
    },
    items,
  };
};

export const getPendingOrders = async (limit = 100) => {
  const response = await platformApi.get('/user/orders/pending', {
    params: { limit: toPositiveInteger(limit, 100) },
  });
  return normalizeTradingListResponse(response.data, normalizeOrder);
};

export const getOrderByTicket = async (ticket) => {
  const response = await platformApi.get(
    `/user/orders/${encodeURIComponent(ticket)}`,
  );
  return normalizeSingleResponse(response.data, normalizeOrder);
};

export const getOrder = getOrderByTicket;

export const checkOrder = async (orderData) => {
  const response = await platformApi.post(
    '/user/orders/check',
    normalizeOrderRequest(orderData),
  );
  return normalizeEnvelope(response.data);
};

export const createMarketOrder = async (orderData) => {
  const response = await platformApi.post(
    '/user/orders',
    normalizeOrderRequest(orderData),
  );
  return normalizeEnvelope(response.data);
};

export const createPendingOrder = async (orderData) => {
  const response = await platformApi.post(
    '/user/orders/pending',
    normalizeOrderRequest(orderData),
  );
  return normalizeEnvelope(response.data);
};

export const openOrder = async (orderData) => (
  isPendingOrderPayload(orderData)
    ? createPendingOrder(orderData)
    : createMarketOrder(orderData)
);

export const modifyOrder = async (ticket, payload) => {
  const response = await platformApi.patch(
    `/user/orders/${encodeURIComponent(ticket)}`,
    payload,
  );
  return normalizeEnvelope(response.data);
};

export const deleteOrder = async (ticket) => {
  const response = await platformApi.delete(
    `/user/orders/${encodeURIComponent(ticket)}`,
  );
  return normalizeEnvelope(response.data);
};

export const closeOrder = deleteOrder;

export const getPositions = async () => {
  const response = await platformApi.get('/user/positions');
  return normalizeTradingListResponse(response.data, normalizePosition);
};

export const getPositionByTicket = async (ticket) => {
  const response = await platformApi.get(
    `/user/positions/${encodeURIComponent(ticket)}`,
  );
  return normalizeSingleResponse(response.data, normalizePosition);
};

export const getPosition = getPositionByTicket;

export const closePosition = async (ticket, payload = {}) => {
  const response = await platformApi.post(
    `/user/positions/${encodeURIComponent(ticket)}/close`,
    toClosePayload(payload),
  );
  return normalizeEnvelope(response.data);
};

export const partialClosePosition = async (ticket, payload) => {
  const response = await platformApi.post(
    `/user/positions/${encodeURIComponent(ticket)}/partial-close`,
    toClosePayload(payload),
  );
  return normalizeEnvelope(response.data);
};

export const modifyPosition = async (ticket, payload) => {
  const response = await platformApi.post(
    `/user/positions/${encodeURIComponent(ticket)}/modify`,
    payload,
  );
  return normalizeEnvelope(response.data);
};

// The API does not register a separate account-history route. Keep the legacy
// method useful by returning the authoritative deal history.
export const getAccountHistory = getDealsHistory;

export const loadFavouritesFromStorage = async () => {
  try {
    const raw = localStorage.getItem('@favourites');
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('[platformService] loadFavouritesFromStorage:', error.message);
    return [];
  }
};

const platformService = {
  login,
  logout,
  refreshToken,
  getHealth,
  health,
  getPublicSymbols,
  getSymbol,
  getPublicLatestPrice,
  getLastPrice,
  getPriceSnapshot,
  getTimeframes,
  getChartTimeframes,
  getCandles,
  getChartHistory,
  getTicks,
  getChartTicks,
  getProfile,
  getAccountMetrics,
  getDetails,
  getBalanceEquity,
  getSymbolList,
  getGroupSymbols,
  getDeals,
  getDealsHistory,
  getUserLatestPrice,
  getCloseOrders,
  getClosedOrders,
  getOrderHistory,
  getPendingOrders,
  getOrderByTicket,
  getOrder,
  checkOrder,
  createMarketOrder,
  createPendingOrder,
  openOrder,
  modifyOrder,
  deleteOrder,
  closeOrder,
  getPositions,
  getPositionByTicket,
  getPosition,
  closePosition,
  partialClosePosition,
  modifyPosition,
  getAccountHistory,
  loadFavouritesFromStorage,
};

export default platformService;
